import { useEffect, useState } from 'react'
import type { Product } from 'src/components/restructure/product/types/product'
import { Picture } from 'src/components/ui/Image'
import { clientStore } from 'src/services/axiosInstance'
import { useMobile } from 'src/hooks/useMobile'
import { InView } from 'react-intersection-observer'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude/dispatchAmplitudeEvent'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

import { ProductCard } from './productCard'

function useProductById(productId: string) {
  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!productId) {
      setProduct(null)
      setLoading(false)

      return
    }

    let cancelled = false

    setLoading(true)
    setError(null)

    clientStore
      .get<Product>(`/v2/api/decathlon-pdp/product?productId=${productId}`)
      .then((response) => {
        if (cancelled) return

        setProduct({
          ...response.data,
          items: response.data.items.filter((item) => !item.isSimilarProduct),
        })
      })
      .catch((err) => {
        if (cancelled) return

        console.error(`Erro ao buscar produto ${productId}:`, err)
        setProduct(null)
        setError(err)
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [productId])

  return { product, loading, error }
}

type TechnologyItem = {
  title: string
  text: string
  icon: string
}

type Props = {
  enable?: boolean
  backgroundColor?: string
  leftTitle: string
  leftImageDesktop: string
  leftImageMobile: string
  rightTitle: string
  rightText: string
  productId: string
  dataSection?: string
  locationOnPage?: string
  technologies?: TechnologyItem[]
}

function BannerProductInfos({
  enable = true,
  backgroundColor = '#15181B',
  leftTitle,
  leftImageDesktop,
  leftImageMobile,
  rightTitle,
  rightText,
  productId,
  technologies = [],
  dataSection = 'uv-technology-highlight',
  locationOnPage,
}: Props) {
  const { product, loading, error } = useProductById(enable ? productId : '')
  const { isMobile } = useMobile()
  const { pageData } = useGlobalContext()

  const firstAvailableItem = product?.items.find((item) => item.isAvailable)

  if (!enable) return null

  const sendPromoFeatureViewedEvent = () => {
    dispatchAmplitudeEvent({
      eventName: 'Promo Feature Viewed',
      eventData: {
        ...getStandardObject(pageData),
        ...getFeatureObject({
          section: 'Informações do Produto',
          locationOnPage: locationOnPage ?? null,
          name: 'Informações do Produto',
          index: 0,
        }),
      },
    })
  }

  return (
    <InView
      as="section"
      triggerOnce
      style={{ backgroundColor }}
      className="mx-auto w-full text-white"
      data-section={dataSection}
      onChange={(inView) => {
        if (inView) {
          sendPromoFeatureViewedEvent()
        }
      }}
    >
      <div className="mx-auto max-w-[1550px] px-4 py-7 md:p-10">
        <h2 className="mb-4 text-[4rem] font-bold leading-none text-white md:mb-6 md:text-[clamp(3.5rem,7vw,7.5rem)] md:font-medium">
          {leftTitle}
        </h2>
        <div className="flex flex-col gap-8 md:grid md:grid-cols-[52%_48%] md:gap-0">
          <div className="relative md:pr-[3.0625rem]">
            <div className="relative w-full max-h-[38.125rem]">
              <Picture
                className="w-full object-cover max-h-[38.125rem] max-w-[30.5rem] md:max-w-[29rem]"
                sources={[
                  {
                    src: leftImageMobile ?? '',
                    width: 640,
                    height: 820,
                    media: '(max-width: 767px)',
                  },
                  {
                    src: leftImageDesktop ?? '',
                    width: 488,
                    height: 610,
                    media: '(min-width: 768px)',
                  },
                ]}
                img={{
                  src: leftImageDesktop ?? '',
                  width: 488,
                  height: 610,
                  alt: leftTitle,
                  loading: 'lazy',
                }}
                shouldCompress={false}
              />
              <div className="hidden md:absolute md:right-0 md:top-[3.75rem] md:block md:w-[17.4375rem]">
                <ProductCard
                  loading={loading}
                  error={error}
                  product={product}
                  firstAvailableItem={firstAvailableItem}
                  locationOnPage={locationOnPage}
                />
              </div>
            </div>
          </div>

          <div className="border-white/15 pt-6  md:border-l md:border-t-0 md:pl-12 md:pt-0">
            {rightTitle && isMobile ? (
              <h3 className="mb-4 text-font-size-7 font-semibold leading-[1.25] text-white md:text-font-size-8">
                {rightTitle}
              </h3>
            ) : null}

            <p className="mb-6 max-w-[33.75rem] text-body-sm leading-[1.6] text-white/65 md:mb-10">
              {rightText}
            </p>

            <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto scrollbar-line-light px-4 pb-2 md:mx-0 md:grid md:grid-cols-2 md:gap-x-6 md:gap-y-0 md:overflow-visible md:px-0 md:pb-0">
              {technologies?.slice(0, 4).map((tech, index) => (
                <article
                  key={`${tech.title}-${index}`}
                  className="w-72 flex-shrink-0 snap-start md:border-t border-white/15 py-5 md:w-auto md:flex-shrink md:py-6"
                >
                  <header className="mb-2 flex justify-start gap-4 items-center ">
                    <h4 className="md:text-[28px]  sm:text-[18px] font-semibold leading-[1.25] text-white">
                      {tech.title}
                    </h4>

                    {tech.icon && (
                      <img
                        src={tech.icon}
                        alt=""
                        className="md:h-8 md:w-8 w-4 h-4 flex-shrink-0 object-contain"
                        loading="lazy"
                      />
                    )}
                  </header>

                  <p className="text-[12.5px] md:text-body-sm leading-[1.6] text-white/60 md:text-white/75">
                    {tech.text}
                  </p>
                </article>
              ))}
            </div>
          </div>

          <div className="md:hidden">
            <ProductCard
              loading={loading}
              error={error}
              product={product}
              firstAvailableItem={firstAvailableItem}
              locationOnPage={locationOnPage}
            />
          </div>
        </div>
      </div>
    </InView>
  )
}

export default BannerProductInfos
